import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@pkp-g/core';
import { LanguageSelectorComponent } from '@pkp-g/shared';
import { LocalizationPipe } from '@shared/modules/localization';
import { MessagesService } from '@shared/modules/messages';
import { FormBuilderService, IFormGroupConfig, UIButton, UIFormModule } from '@shared/ui';
import { catchError, EMPTY } from 'rxjs';
import { authFormConfig } from './configs';

@Component({
  selector: 'pkp-g-auth-page',
  standalone: true,
  imports: [
    UIButton,
    UIFormModule,
    ReactiveFormsModule,
    LocalizationPipe,
    LanguageSelectorComponent,
  ],
  providers: [LocalizationPipe],
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
})
export class AuthPage implements OnInit {
  isFormSubmitting = false;

  form?: FormGroup;
  formConfig: IFormGroupConfig = authFormConfig;

  get isFormInvalid(): boolean {
    return !!this.form?.invalid;
  }

  constructor(
    private formBuilder: FormBuilderService,
    private router: Router,
    private authService: AuthService,
    private destroy$: DestroyRef,
    private messagesService: MessagesService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.build(this.formConfig);
  }

  setErrorsRecursively(form: FormGroup | FormArray, error: ValidationErrors): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);

      if (control instanceof FormControl) {
        control.setErrors(error);
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.setErrorsRecursively(control, error);
      }
    });
  }

  onSubmit(): void {
    if (this.isFormSubmitting || this.isFormInvalid) {
      return;
    }

    this.isFormSubmitting = true;
    this.form?.disable();
    const payload = this.form?.getRawValue();

    this.authService
      .onLogin(payload)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        catchError(() => {
          this.isFormSubmitting = false;
          this.form?.enable();
          this.messagesService.showError('BASE.UNEXPECTED_ERROR');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.router.navigate(['']);
      });
  }
}
